<template>
    <v-container fluid class="py-6 px-6 security-page">
        <v-row>
            <v-col cols="12">
                <div class="home-like-header mb-4">
                    <h2 class="text-h5 mb-1">Two-factor authentication</h2>
                    <p class="mb-0 security-help-text">
                        When enabled, logging in also requires a 6-digit code sent to your email address. You can remember a device for 7 days so you are not asked every time.
                    </p>
                </div>
            </v-col>
        </v-row>

        <v-row align="stretch">
            <v-col cols="12" md="6" lg="6">
                <v-card outlined class="pa-4 security-card">
                    <div class="d-flex align-center mb-3">
                        <v-chip :color="enabled ? 'green' : 'grey lighten-1'" :text-color="enabled ? 'white' : 'black'" small class="mr-3">
                            {{ enabled ? "Enabled" : "Disabled" }}
                        </v-chip>
                        <span v-if="enforcedByCompany" class="security-help-text">Required by your company</span>
                    </div>

                    <template v-if="!enabled">
                        <p class="mb-3 security-help-text">Two-factor authentication is currently off for your account.</p>
                        <v-btn color="primary" rounded @click="startEnable" :loading="loading" :disabled="loading">Enable two-factor authentication</v-btn>
                    </template>
                    <template v-else>
                        <p class="mb-3 security-help-text">Two-factor authentication is on. You will be asked for a code the next time you log in from a new device.</p>
                        <v-btn v-if="!enforcedByCompany" color="error" outlined rounded @click="showDisableDialog = true" :disabled="loading">Disable two-factor authentication</v-btn>
                        <p v-else class="mb-0 security-help-text">Your company requires two-factor authentication, so it cannot be disabled here.</p>
                    </template>
                </v-card>
            </v-col>

            <v-col v-if="isAdmin" cols="12" md="6" lg="6" class="d-flex">
                <v-card outlined class="pa-4 security-card">
                    <div class="font-weight-bold mb-2">Company policy</div>
                    <p class="mb-3 security-help-text">Require two-factor authentication for every user in this company, including admins.</p>
                    <v-switch v-model="enforcedByCompany" label="Require two-factor authentication for all users" :loading="enforceLoading" :disabled="enforceLoading" @change="onEnforceChange" />
                </v-card>
            </v-col>
        </v-row>

        <!-- Enable: Code aus der Mail bestaetigen -->
        <v-dialog v-model="showEnableDialog" max-width="420" persistent>
            <v-card>
                <v-card-title class="headline">Confirm two-factor authentication</v-card-title>
                <v-card-text>
                    We sent a 6-digit code to your email address. Enter it below to turn two-factor authentication on.
                    <v-text-field
                        v-model="enableCode"
                        class="mt-4"
                        label="Verification code*"
                        inputmode="numeric"
                        maxlength="6"
                        autocomplete="one-time-code"
                        :error="Boolean(enableError)"
                        :error-messages="enableError ? [enableError] : []" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeEnableDialog">Cancel</v-btn>
                    <v-btn color="primary" rounded @click="confirmEnable" :loading="loading" :disabled="loading">Confirm</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Disable: Passwort bestaetigen -->
        <v-dialog v-model="showDisableDialog" max-width="420">
            <v-card>
                <v-card-title class="headline">Disable two-factor authentication?</v-card-title>
                <v-card-text>
                    Your account will only require your password to log in from then on. Enter your current password to confirm.
                    <v-text-field
                        v-model="disablePassword"
                        class="mt-4"
                        type="password"
                        label="Current password*"
                        autocomplete="current-password"
                        :error="Boolean(disableError)"
                        :error-messages="disableError ? [disableError] : []" />
                    <v-checkbox v-model="confirmDisable" class="mt-2" hide-details label="I confirm that I want to disable two-factor authentication." />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDisableDialog">Cancel</v-btn>
                    <v-btn color="error" rounded @click="confirmDisableTwoFactor" :loading="loading" :disabled="!confirmDisable || loading">Disable</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    export default {
        data() {
            return {
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH),
                loading: false,
                enabled: false,
                enforcedByCompany: false,
                enforceLoading: false,
                showEnableDialog: false,
                enableChallengeId: null,
                enableCode: "",
                enableError: null,
                showDisableDialog: false,
                disablePassword: "",
                confirmDisable: false,
                disableError: null
            }
        },
        computed: {
            isAdmin() {
                return !!(this.$store && this.$store.state && this.$store.state.admin)
            }
        },
        methods: {
            createInfo(message, color, icon) {
                this.$emit("info", message, color, icon)
            },
            authBody() {
                // token: das Backend prueft die 2FA-Settings-Routen zusaetzlich gegen das Session-Token,
                // nicht nur gegen die verschluesselten IDs.
                return { user_id: localStorage.id, company_id: localStorage.company_id, token: localStorage.token }
            },
            handleError(error, fallbackMessage) {
                console.log(error)
                if (error && error.status === 403) {
                    this.createInfo("You are not allowed to manage two-factor authentication for this company.", "#fcba03", "mdi-alert-circle-outline")
                } else if (error && error.status === 429) {
                    const retryAfter = error.body && error.body.retry_after
                    this.createInfo(retryAfter ? "Too many attempts. Please try again in " + retryAfter + "s." : "Too many attempts. Please try again later.", "#fcba03", "mdi-alert-circle-outline")
                } else {
                    this.createInfo(fallbackMessage, "#fcba03", "mdi-alert-circle-outline")
                }
            },
            fetchStatus() {
                this.loading = true
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/user/2fa/status", this.authBody(), { headers: { Authorization: this.auth } })
                    .then((response) => {
                        this.enabled = !!(response.body && response.body.enabled)
                        this.enforcedByCompany = !!(response.body && response.body.enforced_by_company)
                    })
                    .catch((error) => this.handleError(error, "Failed to load two-factor status."))
                    .finally(() => {
                        this.loading = false
                    })
            },
            startEnable() {
                this.loading = true
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/user/2fa/enable", this.authBody(), { headers: { Authorization: this.auth } })
                    .then((response) => {
                        this.enableChallengeId = response.body && response.body.challenge_id
                        this.enableCode = ""
                        this.enableError = null
                        this.showEnableDialog = true
                    })
                    .catch((error) => this.handleError(error, "Could not send a verification code."))
                    .finally(() => {
                        this.loading = false
                    })
            },
            closeEnableDialog() {
                this.showEnableDialog = false
                this.enableCode = ""
                this.enableError = null
            },
            confirmEnable() {
                if (!/^\d{6}$/.test(this.enableCode.trim())) {
                    this.enableError = "Enter the 6-digit code."
                    return
                }
                this.loading = true
                this.enableError = null
                this.$http
                    .post(
                        process.env.VUE_APP_APIURL + "/user/2fa/enable/confirm",
                        Object.assign({}, this.authBody(), { challenge_id: this.enableChallengeId, code: this.enableCode.trim() }),
                        { headers: { Authorization: this.auth } }
                    )
                    .then((response) => {
                        this.enabled = !!(response.body && response.body.enabled)
                        this.closeEnableDialog()
                        this.createInfo("Two-factor authentication is now enabled.", "green", "mdi-check-circle-outline")
                    })
                    .catch((error) => {
                        console.log(error)
                        this.enableError = this.twoFactorErrorMessage(error && error.body && error.body.twofactor_error)
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            closeDisableDialog() {
                this.showDisableDialog = false
                this.disablePassword = ""
                this.confirmDisable = false
                this.disableError = null
            },
            confirmDisableTwoFactor() {
                if (!this.confirmDisable) return
                this.loading = true
                this.disableError = null
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/user/2fa/disable", Object.assign({}, this.authBody(), { pass: this.disablePassword }), { headers: { Authorization: this.auth } })
                    .then((response) => {
                        this.enabled = !!(response.body && response.body.enabled)
                        this.closeDisableDialog()
                        this.createInfo("Two-factor authentication is now disabled.", "green", "mdi-check-circle-outline")
                    })
                    .catch((error) => {
                        console.log(error)
                        if (error && error.status === 401 && error.body && error.body.error === "invalid session") {
                            this.disableError = "Your session has expired. Please log in again."
                        } else if (error && error.status === 401) {
                            this.disableError = "Wrong password."
                        } else if (error && error.status === 403) {
                            this.disableError = "Your company requires two-factor authentication."
                        } else {
                            this.disableError = "Could not disable two-factor authentication."
                        }
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            onEnforceChange(value) {
                this.enforceLoading = true
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/user/2fa/enforce", Object.assign({}, this.authBody(), { enforce: !!value }), { headers: { Authorization: this.auth } })
                    .then((response) => {
                        this.enforcedByCompany = !!(response.body && response.body.enforced_by_company)
                        this.createInfo(
                            this.enforcedByCompany ? "Two-factor authentication is now required for everyone in this company." : "Two-factor authentication is no longer enforced company-wide.",
                            "green",
                            "mdi-check-circle-outline"
                        )
                    })
                    .catch((error) => {
                        // Zustand zuruecksetzen, wenn der Request fehlschlaegt - der Switch soll nicht
                        // etwas anzeigen, das serverseitig nicht uebernommen wurde.
                        this.enforcedByCompany = !value
                        this.handleError(error, "Could not update the company policy.")
                    })
                    .finally(() => {
                        this.enforceLoading = false
                    })
            },
            twoFactorErrorMessage(error) {
                if (error == "wrong_code") return "Incorrect code."
                if (error == "expired") return "This code has expired. Please request a new one."
                if (error == "too_many_attempts") return "Too many attempts. Please request a new code."
                if (error == "invalid_challenge") return "This code is no longer valid. Please try again."
                return "Something went wrong. Please try again."
            }
        },
        mounted() {
            this.fetchStatus()
        }
    }
</script>

<style scoped>
    .security-help-text {
        color: #4c4c4c;
    }

    .security-page {
        max-width: 1360px;
    }

    .home-like-header {
        background: linear-gradient(90deg, rgb(245 254 251 / 90%) 0%, rgb(249 245 247 / 90%) 100%);
        border: 1px solid #e9efed;
        border-radius: 12px;
        padding: 14px 16px;
    }

    .security-card {
        border-radius: 12px;
        width: 100%;
    }
</style>
